import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Terminal, User } from 'lucide-react';
// Typewriter effect component
const TypewriterText = ({
  text,
  delay = 0
}: {
  text: string;
  delay?: number;
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [started, setStarted] = useState(false);
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (started) {
      let i = 0;
      const interval = setInterval(() => {
        setDisplayedText(text.substring(0, i));
        i++;
        if (i > text.length) clearInterval(interval);
      }, 20);
      return () => clearInterval(interval);
    } else {
      timeout = setTimeout(() => setStarted(true), delay);
      return () => clearTimeout(timeout);
    }
  }, [text, delay, started]);
  return <span data-id="element-252">
      {displayedText}
      <motion.span animate={{
      opacity: [1, 0]
    }} transition={{
      repeat: Infinity,
      duration: 0.8
    }} className="inline-block w-1.5 h-3.5 bg-primary ml-0.5 align-middle" data-id="element-253" />
    </span>;
};
export function AISection() {
  return <section className="py-24 relative overflow-hidden" data-id="element-254">
      <div className="max-w-7xl mx-auto px-6" data-id="element-255">
        <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-256">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-257">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 border border-primary/20 shadow-[0_0_15px_rgba(245,158,11,0.2)]" data-id="element-258">
              <Sparkles className="w-4 h-4 animate-pulse" data-id="element-259" />
              AI Assistant
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight" data-id="element-260">
              Talk to Booker like a booking assistant.
            </h2>
            <p className="text-lg text-textMuted leading-relaxed mb-8" data-id="element-261">
              Use Booker through the full interface or simply tell it what you
              want: plan a route, draft outreach, summarize offers, check lineup
              conflicts, or prepare contracts. Same workflow engine. Faster
              control surface.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="glow-border-wrapper rounded-xl shadow-2xl" data-id="element-262">
            <div className="glow-border-content bg-[#0F0F14] overflow-hidden flex flex-col scan-line" data-id="element-263">
              <div className="h-10 border-b border-border flex items-center px-4 bg-surface gap-2 relative z-20" data-id="element-264">
                <Terminal className="w-4 h-4 text-textMuted" data-id="element-265" />
                <span className="text-xs font-medium text-textMuted" data-id="element-266">
                  Booker Command
                </span>
              </div>

              <div className="p-6 flex flex-col gap-6 font-mono text-sm relative z-20" data-id="element-267">
                {/* Prompt 1 */}
                <motion.div initial={{
                opacity: 0
              }} whileInView={{
                opacity: 1
              }} viewport={{
                once: true
              }} className="flex gap-4" data-id="element-268">
                  <div className="w-6 h-6 rounded bg-surface border border-border flex items-center justify-center shrink-0" data-id="element-269">
                    <User className="w-3 h-3 text-textMuted" data-id="element-270" />
                  </div>
                  <div className="text-textMain pt-1" data-id="element-271">
                    Plan a June route from Michigan to Maine
                  </div>
                </motion.div>
                <div className="flex gap-4" data-id="element-272">
                  <div className="w-6 h-6 rounded bg-primary/20 border border-primary/30 flex items-center justify-center shrink-0 shadow-[0_0_10px_rgba(245,158,11,0.3)]" data-id="element-273">
                    <Sparkles className="w-3 h-3 text-primary animate-pulse" data-id="element-274" />
                  </div>
                  <div className="text-textMuted pt-1 leading-relaxed" data-id="element-275">
                    <TypewriterText text="I've generated a 14-date route prioritizing 200-500 cap rooms. The route avoids Mondays and builds in two travel days." delay={500} data-id="element-276" />
                    <motion.span initial={{
                    opacity: 0
                  }} whileInView={{
                    opacity: 1
                  }} transition={{
                    delay: 3.5
                  }} className="text-primary underline decoration-primary/30 cursor-pointer ml-1 hover:text-primaryHover" data-id="element-277">
                      Review route →
                    </motion.span>
                  </div>
                </div>

                {/* Prompt 2 */}
                <motion.div initial={{
                opacity: 0
              }} whileInView={{
                opacity: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: 4
              }} className="flex gap-4 mt-4" data-id="element-278">
                  <div className="w-6 h-6 rounded bg-surface border border-border flex items-center justify-center shrink-0" data-id="element-279">
                    <User className="w-3 h-3 text-textMuted" data-id="element-280" />
                  </div>
                  <div className="text-textMain pt-1" data-id="element-281">
                    Draft outreach for the top 25 folk venues
                  </div>
                </motion.div>
                <div className="flex gap-4" data-id="element-282">
                  <div className="w-6 h-6 rounded bg-primary/20 border border-primary/30 flex items-center justify-center shrink-0 shadow-[0_0_10px_rgba(245,158,11,0.3)]" data-id="element-283">
                    <Sparkles className="w-3 h-3 text-primary animate-pulse" data-id="element-284" />
                  </div>
                  <div className="text-textMuted pt-1 leading-relaxed" data-id="element-285">
                    <TypewriterText text="Drafted 25 personalized emails using your 'Summer Run' template. Ready for your review before sending." delay={4500} data-id="element-286" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}